import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Book } from "lucide-react";
import OrderManager from "../../services/dashboard/OrderManager";

export default function MostRentedBooks() {
  const [mostRented, setMostRented] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchMostRented() {
      try {
        const data = await OrderManager.getMostRentedBooks(10);
        setMostRented(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching most rented books:", err);
        setMostRented([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchMostRented();
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Most Rented Books
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Top performing titles</p>
          </div>
          <TrendingUp size={16} className="text-gray-400" />
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-3"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading rental data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (mostRented.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Most Rented Books
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Top performing titles</p>
          </div>
          <TrendingUp size={16} className="text-gray-400" />
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 mb-3">
              <Book size={20} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">No rental activity yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Data will appear as books are rented</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Most Rented Books
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Top performing titles</p>
        </div>
        <TrendingUp size={16} className="text-gray-400" />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                Rank
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                Book Title
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                Rentals
              </th>
            </tr>
          </thead>
          <tbody>
            {mostRented.map((book, index) => (
              <tr
                key={book.bookId}
                className="border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
              >
                <td className="px-3 py-3 whitespace-nowrap">
                  {index < 3 ? (
                    <span
                      className={`
                        inline-flex items-center justify-center w-6 h-6 rounded-md font-semibold text-xs
                        ${
                          index === 0
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                            : index === 1
                            ? "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            : "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400"
                        }
                      `}
                    >
                      {index + 1}
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-gray-400 dark:text-gray-500 ml-1">
                      {index + 1}
                    </span>
                  )}
                </td>

                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <Book size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-900 dark:text-white truncate">
                      {book.bookName}
                    </span>
                    {index < 3 && (
                      <span className="text-xs">🔥</span>
                    )}
                  </div>
                </td>

                <td className="px-3 py-3 text-right">
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {book.rentalCount}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
